'use client';
import React, { useEffect, useRef } from 'react';
import AppImage from '@/components/ui/AppImage';

const services = [
  {
    id: 'iot',
    tag: 'Layanan 01',
    title: 'Integrasi Perangkat IoT Pertanian',
    subtitle: 'Sensor cuaca · Kelembaban · pH · TDS',
    body: 'Pengadaan dan pemasangan sensor pertanian berbasis IoT seperti sensor cuaca, kelembaban, pH, TDS, dan lain-lain yang dapat diintegrasikan dengan sistem monitoring jarak jauh untuk efisiensi pengelolaan pertanian.',
    proof: [
      'Sensor suhu, kelembaban, pH, TDS, curah hujan',
      'Integrasi dengan dashboard monitoring',
      'Pemasangan dan konsultasi teknis tersedia',
    ],
    image: '/assets/images/services/integrasi.webp',
    imageAlt: 'Perangkat sensor IoT pertanian terpasang di lahan pertanian modern',
  },
  {
    id: 'monitoring',
    tag: 'Layanan 02',
    title: 'Sistem Monitoring dan Kendali Jarak Jauh',
    subtitle: 'Dashboard real-time · Kontrol irigasi · Hidroponik Otomatis',
    body: 'Integrasi dengan platform IoT dan dikembangkan lebih lanjut, memungkinkan pemantauan melalui aplikasi mobile dan web. Kendalikan irigasi, ventilasi, dan sistem pertanian lainnya dari mana saja.',
    proof: [
      'Dashboard monitoring real-time 24/7',
      'Kontrol irigasi dan ventilasi jarak jauh',
      'Akses via mobile app dan web browser',
    ],
    image: '/assets/images/services/dashboard.webp',
    imageAlt: 'Dashboard monitoring pertanian cerdas dengan data real-time',
  },
  {
    id: 'alat',
    tag: 'Layanan 03',
    title: 'Penyedia Alat dan Bahan Pertanian',
    subtitle: 'Paket irigasi · Vertigasi · Pompa',
    body: 'Paket irigasi/vertigasi yang terdiri dari selang/perpipaan irigasi, pompa, dan tandon air. Dapat diintegrasikan dengan sistem otomatisasi untuk efisiensi maksimal.',
    proof: [
      'Paket irigasi lengkap siap pasang',
      'Pompa air berkapasitas berbagai ukuran',
      'Kompatibel dengan sistem otomatisasi IoT',
    ],
    image: '/assets/images/services/product.webp',
    imageAlt: 'Alat dan bahan pertanian untuk sistem irigasi modern',
  },
  {
    id: 'komunitas',
    tag: 'Layanan 04',
    title: 'Komunitas Pengembangan Smart Farming',
    subtitle: 'Kolaborasi · Riset bersama',
    body: 'Membuka akses bagi siapa pun yang ingin belajar, bergabung, dan berkontribusi dalam komunitas pengembang smart farming. Terbuka untuk petani digital, dam pelajar/mahasiswa dalam pengembang hardware/software.',
    proof: [
      'Platform berbagi pengetahuan terbuka',
      'Riset kolaboratif dan pengembangan proyek',
      'Akses data nyata dari lapangan',
      'Networking dengan petani dan pengembang',
    ],
    image: '/assets/images/services/komunitas.png',
    imageAlt: 'Komunitas pengembang teknologi pertanian berkumpul untuk diskusi',
  },
  {
    id: 'pelatihan',
    tag: 'Layanan 05',
    title: 'Program Pelatihan',
    subtitle: 'Online/Offline · Hands-on · Kurikulum terstruktur',
    body: 'Pelatihan petani untuk memahami dan menggunakan teknologi pertanian berbasis data. Tersedia metode online dan offline dengan panduan mentor profesional.',
    proof: [
      'Metode online, offline, hybrid.',
      'Materi dan praktik step-by-step untuk pemula',
      'Sertifikat kompetensi',
    ],
    image: '/assets/images/services/pelatihan.jpeg',
    imageAlt: 'Pelatihan smart farming untuk petani dengan perangkat IoT',
  },
  {
    id: 'konsultasi',
    tag: 'Layanan 06',
    title: 'Jasa Konsultasi Smart Farming',
    subtitle: 'Perencanaan · Implementasi · Evaluasi',
    body: 'Butuh panduan mengimplementasikan pertanian cerdas? Kami siap membantu dari perencanaan hingga eksekusi dengan pendekatan berbasis data dan teknologi. Konsultasi mencakup analisis kebutuhan, desain sistem, dan pendampingan implementasi.',
    proof: [
      'Konsultasi perencanaan smart farming',
      'Desain sistem sesuai kebutuhan',
      'Pendampingan implementasi hingga evaluasi',
      'Pendekatan berbasis data dan teknologi',
    ],
    image: '/assets/images/services/konsultasi.png',
    imageAlt: 'Tim konsultan memberikan pendampingan smart farming di lapangan',
  },
];

export default function ServicesSection() {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1 }
    );
    const els = ref?.current?.querySelectorAll('.reveal, .reveal-left, .reveal-right');
    els?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  return (
    <section id="services" ref={ref} className="relative bg-pond blueprint-grid py-24 md:py-32 px-6 md:px-10">
      <div className="max-w-7xl mx-auto">
        <div className="reveal flex items-center gap-4 mb-4">
          <div className="w-12 h-px bg-chartreuse opacity-60" />
          <span className="annotation">LAYANAN DAN PROGRAM UTAMA</span>
        </div>
        <h2 className="reveal delay-100 font-mono font-bold text-4xl md:text-5xl text-text-primary mb-4 leading-tight">
          SOLUSI<br />
          <span className="text-chartreuse">PERTANIAN MODERN</span>
        </h2>
        <p className="reveal delay-200 text-text-muted text-lg mb-16 max-w-2xl leading-relaxed">
          Dari teknologi smart farming hingga pelatihan digital, menyediakan ekosistem lengkap
          untuk pertanian cerdas.
        </p>
        <div className="flex flex-col gap-16">
          {services.map((svc, i) => {
            const isEven = i % 2 === 0;
            return (
              <div
                key={svc.id}
                className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
                <div className={`${isEven ? 'lg:order-1' : 'lg:order-2'}`}>
                  <div className={`reveal-${isEven ? 'left' : 'right'} relative overflow-hidden rounded-sm`}>
                    <div className="absolute top-0 left-0 w-5 h-5 border-t border-l border-chartreuse/40 z-10" />
                    <div className="absolute top-0 right-0 w-5 h-5 border-t border-r border-chartreuse/40 z-10" />
                    <div className="absolute bottom-0 left-0 w-5 h-5 border-b border-l border-chartreuse/40 z-10" />
                    <div className="absolute bottom-0 right-0 w-5 h-5 border-b border-r border-chartreuse/40 z-10" />
                    <AppImage
                      src={svc.image}
                      alt={svc.imageAlt}
                      width={700}
                      height={480}
                      className="w-full h-[300px] md:h-[380px] object-contain lifecycle-img brightness-80" />
                    <div className="absolute top-4 left-4 z-10 glass-card px-3 py-1.5 rounded-sm">
                      <span className="annotation text-chartreuse">{svc.tag}</span>
                    </div>
                  </div>
                </div>
                <div className={`${isEven ? 'lg:order-2' : 'lg:order-1'}`}>
                  <div className={`reveal-${isEven ? 'right' : 'left'}`}>
                    <div className="flex items-center gap-3 mb-4">
                      <span className="font-mono text-[10px] tracking-[0.4em] uppercase text-chartreuse opacity-70">
                        {svc.tag}
                      </span>
                      <div className="flex-1 h-px bg-chartreuse/20" />
                    </div>
                    <h3 className="font-mono font-bold text-2xl md:text-3xl text-text-primary mb-2">
                      {svc.title}
                    </h3>
                    <p className="font-mono text-fern text-sm mb-5 tracking-wide">
                      {svc.subtitle}
                    </p>
                    <p className="text-text-muted leading-relaxed text-base mb-6">
                      {svc.body}
                    </p>
                    <ul className="flex flex-col gap-2 mb-8">
                      {svc.proof.map((item) =>
                      <li key={item} className="flex items-start gap-3">
                          <span className="text-chartreuse font-mono text-xs mt-0.5 shrink-0">→</span>
                          <span className="text-text-muted text-sm leading-relaxed">{item}</span>
                        </li>
                      )}
                    </ul>
                    <a
                      href="/quote"
                      className="btn-chartreuse px-6 py-3 rounded-sm text-xs uppercase tracking-widest inline-block">
                      Tanyakan tentang layanan ini →
                    </a>
                  </div>
                </div>
              </div>);
          })}
        </div>
      </div>
      <div className="absolute bottom-0 left-0 right-0 rule-line" />
    </section>);
}
